import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft, Chrome } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Register() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [userType, setUserType] = useState("bar");
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleGoogleRegister = async () => {
    if (!acceptedPrivacy) {
      toast.error("DEBES ACEPTAR LA POLÍTICA DE PRIVACIDAD");
      return;
    }
    setGoogleLoading(true);
    try {
      const res = await axios.get(`${API}/auth/google/login?user_type=${userType}`);
      // Redirect to Google OAuth
      window.location.href = res.data.auth_url;
    } catch (error) {
      toast.error("Error al iniciar con Google");
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedPrivacy) {
      toast.error("DEBES ACEPTAR LA POLÍTICA DE PRIVACIDAD");
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error("LAS CONTRASEÑAS NO COINCIDEN");
      return;
    }

    if (password.length < 6) {
      toast.error("LA CONTRASEÑA DEBE TENER AL MENOS 6 CARACTERES");
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/register/request`, {
        name,
        email,
        password,
        user_type: userType
      });
      
      if (res.data.email_sent) {
        toast.success("¡EMAIL DE VERIFICACIÓN ENVIADO!");
        navigate("/check-email", { state: { email } });
      } else {
        toast.error(res.data.error || "NO SE PUDO ENVIAR EL EMAIL");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "ERROR AL REGISTRAR");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back Link */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 font-mono text-sm text-zinc-500 hover:text-primary transition-colors mb-8"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          VOLVER
        </Link>

        <div className="bg-[#111] border border-zinc-800 p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <h1 className="text-4xl font-black tracking-tighter">
              <span className="text-white">ROCK</span><span className="text-primary">MAPS</span>
            </h1>
            <p className="font-mono text-sm text-zinc-500 mt-2">CREA TU CUENTA</p>
          </div>

          {/* User Type Toggle */}
          <div className="flex mb-6 border border-zinc-800">
            <button
              type="button"
              onClick={() => setUserType("bar")}
              className={`flex-1 py-3 font-mono text-sm transition-colors ${
                userType === "bar"
                  ? "bg-primary text-white"
                  : "bg-transparent text-zinc-500 hover:text-white"
              }`}
              data-testid="register-type-bar"
            >
              SOY UN BAR
            </button>
            <button
              type="button"
              onClick={() => setUserType("band")}
              className={`flex-1 py-3 font-mono text-sm transition-colors ${
                userType === "band"
                  ? "bg-primary text-white"
                  : "bg-transparent text-zinc-500 hover:text-white"
              }`}
              data-testid="register-type-band"
            >
              SOY UNA BANDA
            </button>
            <button
              type="button"
              onClick={() => setUserType("user")}
              className={`flex-1 py-3 font-mono text-sm transition-colors ${
                userType === "user"
                  ? "bg-primary text-white"
                  : "bg-transparent text-zinc-500 hover:text-white"
              }`}
              data-testid="register-type-user"
            >
              SOY USUARIO
            </button>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="name" className="font-mono text-xs text-zinc-500 mb-2 block">
                {userType === "bar" ? "NOMBRE DEL BAR" : userType === "band" ? "NOMBRE DE LA BANDA" : "TU NOMBRE"}
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="input-ruido w-full"
                placeholder={userType === "bar" ? "El Sótano Rock" : userType === "band" ? "Los Rockeros" : "Tu nombre"}
                data-testid="register-name"
              />
            </div>

            <div>
              <Label htmlFor="email" className="font-mono text-xs text-zinc-500 mb-2 block">
                EMAIL
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="input-ruido w-full"
                placeholder="contacto@bar.com"
                data-testid="register-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="font-mono text-xs text-zinc-500 mb-2 block">
                CONTRASEÑA
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="input-ruido w-full pr-10"
                  placeholder="••••••••"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword" className="font-mono text-xs text-zinc-500 mb-2 block">
                CONFIRMAR CONTRASEÑA
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="input-ruido w-full"
                placeholder="••••••••"
                data-testid="register-confirm-password"
              />
            </div>

            {/* RGPD Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="privacy"
                checked={acceptedPrivacy}
                onChange={(e) => setAcceptedPrivacy(e.target.checked)}
                className="mt-1 w-4 h-4 accent-primary cursor-pointer"
                data-testid="privacy-checkbox"
              />
              <label htmlFor="privacy" className="text-zinc-400 text-sm cursor-pointer">
                He leído y acepto la{" "}
                <Link to="/privacidad" target="_blank" className="text-primary hover:underline">
                  Política de Privacidad
                </Link>
                , los{" "}
                <Link to="/terminos" target="_blank" className="text-primary hover:underline">
                  Términos y Condiciones
                </Link>{" "}
                y el tratamiento de mis datos personales conforme al RGPD.
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full btn-ruido bg-primary text-white font-mono py-6 border-2 border-primary"
              data-testid="register-submit"
            >
              {loading ? "CREANDO..." : "CREAR CUENTA"}
            </Button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-zinc-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-[#111] px-4 text-zinc-600 font-mono">O REGÍSTRATE CON</span>
            </div>
          </div>

          {/* Google Register Button */}
          <Button
            type="button"
            onClick={handleGoogleRegister}
            disabled={googleLoading}
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-mono py-6 border border-zinc-700 flex items-center justify-center gap-3"
            data-testid="google-register-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {googleLoading ? "CONECTANDO..." : "REGISTRAR CON GOOGLE"}
          </Button>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-zinc-500 text-sm">
              ¿Ya tienes cuenta?{" "}
              <Link to="/login" className="text-primary font-mono hover:underline" data-testid="login-link">
                ENTRA
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
